import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useToast } from '../context/ToastContext';
import { useI18n } from '../context/I18nContext';
import { Star, MessageCircle, ChevronRight, ThumbsDown } from 'lucide-react';
import { Skeleton } from '../components/Skeleton';
import { formatDuration } from '../utils';

function fmtDate(iso, lang) {
  if (!iso) return '—';
  try {
    const locale = lang === 'en' ? 'en-US' : 'nb-NO';
    return new Date(iso).toLocaleString(locale, {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function QualityDashboard() {
  const navigate = useNavigate();
  const toast = useToast();
  const { t, lang } = useI18n();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const { data } = await api.get('/api/calls/quality-ratings');
        if (!cancelled) setCalls(data.calls || []);
      } catch (err) {
        if (!cancelled) toast.error(t('quality_load_error', 'Kunne ikke laste kvalitetsdata'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="quality-dashboard-page">
      <div className="mb-6 flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-accent-yellow/20 flex items-center justify-center flex-shrink-0">
          <Star size={18} className="text-accent-yellow" />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-bold text-white">{t('quality_title', 'Samtalekvalitet')}</h1>
          <p className="text-sm text-slate-400 mt-1">
            {t('quality_subtitle', 'Samtaler hvor du har gitt 👎. Bruk innsikten til å forbedre AI-agenten.')}
          </p>
        </div>
      </div>

      {loading && (
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton height={32} width={32} rounded="rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton height={12} className="w-1/2" />
                <Skeleton height={10} className="w-1/3" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && calls.length === 0 && (
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-10 text-center">
          <div className="text-4xl mb-3">🎉</div>
          <h2 className="font-heading text-lg font-semibold text-white mb-1">{t('quality_empty_title', 'Ingen dårlige samtaler')}</h2>
          <p className="text-sm text-slate-400">{t('quality_empty_subtitle', 'Bra jobbet! Ingen samtaler er flagget som dårlige.')}</p>
        </div>
      )}

      {!loading && calls.length > 0 && (
        <div className="bg-navy-800 border border-navy-600 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-navy-900/50 border-b border-navy-600">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">{t('quality_col_date', 'Dato')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">{t('quality_col_caller', 'Innringer')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">{t('quality_col_duration', 'Varighet')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">{t('quality_col_rating', 'Vurdering')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">{t('quality_col_comment', 'Kommentar')}</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500"></th>
                </tr>
              </thead>
              <tbody>
                {calls.map((c) => {
                  const callId = c.call_id || c._id;
                  const goToCall = () => { if (callId) navigate(`/calls/${callId}`); };
                  return (
                  <tr
                    key={callId || Math.random()}
                    className={`border-b border-navy-700 transition-colors ${callId ? 'hover:bg-navy-700/30 cursor-pointer focus:outline-none focus:bg-navy-700/40 focus:ring-2 focus:ring-accent-blue/50' : 'opacity-70'}`}
                    onClick={goToCall}
                    onKeyDown={(e) => {
                      if (!callId) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        goToCall();
                      }
                    }}
                    tabIndex={callId ? 0 : -1}
                    role={callId ? 'button' : undefined}
                    aria-label={callId ? t('quality_row_aria', 'Vis samtaledetaljer') : undefined}
                    data-testid={`quality-row-${callId || 'unknown'}`}
                  >
                    <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{fmtDate(c.created_at, lang)}</td>
                    <td className="px-4 py-3 text-slate-200">
                      {c.caller_name || c.from_number || '—'}
                      {c.caller_name && c.from_number && (
                        <div className="text-xs text-slate-500 mt-0.5">{c.from_number}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{formatDuration(c.duration_seconds || 0)}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-red-500/15 text-red-400 border border-red-500/30">
                        <ThumbsDown size={12} /> {t('quality_rating_bad', 'Dårlig')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-300 max-w-md">
                      {c.quality_rating_comment ? (
                        <div className="flex items-start gap-2">
                          <MessageCircle size={14} className="text-slate-500 mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-2">{c.quality_rating_comment}</span>
                        </div>
                      ) : (
                        <span className="text-slate-600 italic">{t('quality_no_comment', 'Ingen kommentar')}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight size={16} className="text-slate-500 inline" />
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
